/**
 * MelaHeldLeadsWatcher — global watcher (renders nothing).
 *
 * Polls `/subscription/me` every 60 seconds while the page is visible.
 * When the user's `held_count` drops (held leads were released because
 * a top-up was purchased or the monthly quota reset), toasts the delta.
 *
 * Self-suspends:
 *   - when no Mela token is in localStorage (not signed in)
 *   - when the page is hidden (tab in background)
 *   - if the endpoint returns 401 or fails 3× in a row
 *
 * Mounted once at App root.
 */
import { useEffect, useRef } from 'react';
import { toast } from '../utils/toast';

const API = (process.env.REACT_APP_API_URL || 'https://web-production-3fcca.up.railway.app').replace(/\/+$/, '');
const POLL_MS = 60_000;
const STORAGE_KEY = 'mela_held_count_last';

export default function MelaHeldLeadsWatcher() {
  const failCount = useRef(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const tick = async () => {
      const token = localStorage.getItem('mela_token');
      if (!token) return; // not signed in — nothing to watch
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

      try {
        const r = await fetch(`${API}/api/v1/mela/subscription/me`, {
          headers: { 'X-Mela-Token': token, 'Content-Type': 'application/json' },
        });
        if (r.status === 401) {
          // Stale token — stop polling; the user will see this on next manual
          // load. We don't toast because the user might not know what 401 means.
          if (timer.current) { clearInterval(timer.current); timer.current = null; }
          return;
        }
        if (!r.ok) {
          failCount.current += 1;
          if (failCount.current >= 3 && timer.current) {
            clearInterval(timer.current); timer.current = null;
          }
          return;
        }
        failCount.current = 0;
        const j = await r.json();
        const held = Number(j?.balance?.held_count ?? 0);
        const prevRaw = localStorage.getItem(STORAGE_KEY);
        const prev = prevRaw === null ? null : Number(prevRaw);
        localStorage.setItem(STORAGE_KEY, String(held));
        if (prev !== null && Number.isFinite(prev) && held < prev) {
          const released = prev - held;
          toast(
            released === 1
              ? '🎉 1 held lead just unlocked — check Lead Manager'
              : `🎉 ${released} held leads just unlocked — check Lead Manager`,
            'success',
          );
        }
      } catch {
        failCount.current += 1;
        if (failCount.current >= 3 && timer.current) {
          clearInterval(timer.current); timer.current = null;
        }
      }
    };

    // Immediate first tick (so we capture the baseline on a fresh tab) +
    // recurring interval.
    tick();
    timer.current = setInterval(tick, POLL_MS);

    // Re-poll the moment the tab regains focus so a long-backgrounded session
    // sees fresh state quickly instead of waiting up to a full poll cycle.
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      if (timer.current) clearInterval(timer.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return null;
}
